/**
 * StylizedTextCalloutEditor - Editor component for Stylized Text Callout Block
 *
 * Provides a TipTap-based WYSIWYG editor with the full toolbar (minus font size):
 * - Bold, Italic, Underline, Strikethrough
 * - Font color with WCAG checking
 * - Blockquote, Link
 * - Bulleted list, Numbered list
 * - Clear formatting
 * - Text size selector (Smaller / Standard / Larger)
 */

import { useRef, useState, useMemo } from 'react';
import { EditorContent } from '@tiptap/react';
import {
  sanitizeRichTextContent,
  useTheme,
  type StylizedTextCalloutBlock,
  type TextCalloutTextSize,
} from '@course-authoring/shared-types';
import { createTextCalloutStyles, useIsMobileViewport } from '@course-authoring/ui-blocks';

import { createRichTextEditorExtensions } from './richTextEditorExtensions';
import {
  getInteractionRichTextFieldPresetConfig,
  createTokenRichTextToolbarStyles,
} from './interactionEditorUtils';
import { RichTextToolbar, preventRichTextToolbarMouseDown } from './richTextToolbar';
import { useRichTextFieldEditor } from './useRichTextFieldEditor';

export interface StylizedTextCalloutEditorProps {
  block: StylizedTextCalloutBlock;
  onChange: (updates: Partial<StylizedTextCalloutBlock>) => void;
  autoFocus?: boolean;
  translationMode?: boolean;
}

export function StylizedTextCalloutEditor({
  block,
  onChange,
  autoFocus = false,
  translationMode = false,
}: StylizedTextCalloutEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const textSize = block.textSize ?? 'standard';
  const textCalloutStyles = useMemo(
    () => createTextCalloutStyles(tokens, isMobile, isSmallMobile, textSize),
    [tokens, isMobile, isSmallMobile, textSize]
  );

  const sanitizedContent = useMemo(
    () => sanitizeRichTextContent(block.content),
    [block.content]
  );

  const presetConfig = useMemo(
    () => getInteractionRichTextFieldPresetConfig('fullNoFontSize'),
    []
  );

  const extensions = useMemo(
    () =>
      createRichTextEditorExtensions('Enter callout text...', presetConfig.extensionOptions),
    [presetConfig.extensionOptions]
  );

  const toolbarStyles = useMemo(
    () => createTokenRichTextToolbarStyles(tokens),
    [tokens]
  );

  const editor = useRichTextFieldEditor({
    extensions,
    content: sanitizedContent,
    onContentChange: (content) => onChange({ content }),
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    containerRef,
    editorInlineStyle: `outline: none; min-height: ${isMobile ? '60px' : '100px'};`,
    autoFocus,
    shouldRerenderOnTransaction: true,
  });

  const TEXT_SIZE_OPTIONS: { value: TextCalloutTextSize; label: string }[] = [
    { value: 'smaller', label: 'Smaller' },
    { value: 'standard', label: 'Standard' },
    { value: 'larger', label: 'Larger' },
  ];

  const styles: Record<string, React.CSSProperties> = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
      width: '100%',
    },
    toolbarRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      width: textCalloutStyles.card.width,
      maxWidth: textCalloutStyles.card.maxWidth,
      alignSelf: 'center',
    },
    toolbarWrapper: {
      flex: 1,
    },
    textSizeGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    textSizeLabel: {
      fontSize: '13px',
      fontWeight: 500,
      color: tokens.text.muted,
      whiteSpace: 'nowrap',
    },
    segmentedControl: {
      display: 'flex',
      borderRadius: tokens.radius.sm,
      border: `1px solid ${tokens.borders.default}`,
      overflow: 'hidden',
    },
    segmentButton: {
      padding: '4px 10px',
      fontSize: '12px',
      fontWeight: 500,
      border: 'none',
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.primary,
      cursor: 'pointer',
      borderRight: `1px solid ${tokens.borders.default}`,
      transition: `background-color ${tokens.ui.transitionDefault}, color ${tokens.ui.transitionDefault}`,
    },
    segmentButtonLast: {
      borderRight: 'none',
    },
    segmentButtonActive: {
      backgroundColor: tokens.text.primary,
      color: tokens.backgrounds.app,
    },
    calloutCard: {
      ...textCalloutStyles.card,
      transition: `outline ${tokens.ui.transitionDefault}`,
    },
    calloutCardFocused: {
      outline: `2px solid ${tokens.ui.selectionBorder}`,
      outlineOffset: '4px',
    },
    editor: {
      ...textCalloutStyles.content,
    },
  };

  const contentResetStyles = `
    .text-callout-editor .tiptap p {
      margin: 0;
    }
    .text-callout-editor .tiptap p:not(:last-child) {
      margin-bottom: 0.15em;
    }
  `;

  if (!editor) return <div style={styles.container} />;

  return (
    <div
      ref={containerRef}
      className="text-callout-editor"
      style={styles.container}
    >
      <style>{contentResetStyles}</style>
      {isFocused && (
        <div style={styles.toolbarRow}>
          <div style={styles.toolbarWrapper}>
            <RichTextToolbar
              editor={editor}
              items={presetConfig.toolbarItems}
              toolbarStyle={toolbarStyles.toolbar}
              buttonStyle={toolbarStyles.button}
              activeButtonStyle={toolbarStyles.activeButton}
              dividerStyle={toolbarStyles.divider}
            />
          </div>
        </div>
      )}
      {isFocused && !translationMode && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: `-${tokens.spacing.md}`,
          }}
          onMouseDown={preventRichTextToolbarMouseDown}
        >
          <div style={styles.textSizeGroup}>
            <span style={styles.textSizeLabel}>Text size:</span>
            <div style={styles.segmentedControl}>
              {TEXT_SIZE_OPTIONS.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  onMouseDown={preventRichTextToolbarMouseDown}
                  onClick={() => onChange({ textSize: option.value })}
                  style={{
                    ...styles.segmentButton,
                    ...(index === TEXT_SIZE_OPTIONS.length - 1 ? styles.segmentButtonLast : {}),
                    ...(textSize === option.value ? styles.segmentButtonActive : {}),
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <div
        style={{
          ...styles.calloutCard,
          ...(isFocused ? styles.calloutCardFocused : {}),
        }}
      >
        <EditorContent editor={editor} style={styles.editor} />
      </div>
    </div>
  );
}

StylizedTextCalloutEditor.displayName = 'StylizedTextCalloutEditor';
